import React, { useState, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import CipHeader from "../Components/CipHeader";
import cogoToast from "cogo-toast";
import Loader from "react-loader-spinner";
import moment from "moment";
import { Modal } from "react-responsive-modal";
import epcisService from "../services/epcis-service";
import facilityService from "../services/facility-service";
import EpcisUploadModal from "../Components/EpcisUploadModal";
import "react-responsive-modal/styles.css";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import Barcode from "react-barcode";

const PAGE_SIZE = 25;

const getStatusStyle = (status) => {
  switch ((status || "").toLowerCase()) {
    case "complete":
      return "bg-green-100 text-green-700";
    case "warning":
      return "bg-yellow-100 text-yellow-700";
    case "failed":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const EpcisViewer = ({ history }) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState(null);
  const [allFacilitiesSelected, setAllFacilitiesSelected] = useState(true);
  const [facilityDropdownOpen, setFacilityDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const facilitiesLoadedRef = useRef(false);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  const fetchFiles = async (page, searchTerm, facilityIds) => {
    try {
      setLoading(true);
      const resp = await epcisService.getEpcisFiles(page, PAGE_SIZE, searchTerm, facilityIds || []);
      setFiles(resp?.Files || resp?.files || []);
      setTotalCount(resp?.TotalCount || resp?.totalCount || 0);
      setLoading(false);
    } catch (e) {
      const { hide } = cogoToast.error("Something went wrong! Please try again.", {
        hideAfter: 15,
        onClick: () => {
          hide();
        },
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadFacilities = async () => {
      try {
        const data = await facilityService.getFacilities();
        const facs = data || [];
        setFacilities(facs);
        const allIds = facs.map((f) => f.id);
        setSelectedFacilityIds(allIds);
        facilitiesLoadedRef.current = true;
        setAllFacilitiesSelected(true);
      } catch (e) {
        const { hide } = cogoToast.error("Failed to load facilities.", {
          hideAfter: 15,
          onClick: () => { hide(); },
        });
        setSelectedFacilityIds([]);
      }
    };
    loadFacilities();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setFacilityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedFacilityIds !== null && facilitiesLoadedRef.current) {
      fetchFiles(pageNumber, search, selectedFacilityIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, search, selectedFacilityIds]);

  const handleSearch = () => {
    setPageNumber(1);
    setSearch(searchInput);
  };

  const handleClear = () => {
    setSearchInput("");
    setSearch("");
    setPageNumber(1);
  };

  const handleAllToggle = () => {
    if (allFacilitiesSelected) {
      setSelectedFacilityIds([]);
      setAllFacilitiesSelected(false);
    } else {
      setSelectedFacilityIds(facilities.map((f) => f.id));
      setAllFacilitiesSelected(true);
    }
    setPageNumber(1);
  };

  const handleFacilityToggle = (id) => {
    const newSelected = selectedFacilityIds.includes(id)
      ? selectedFacilityIds.filter((fid) => fid !== id)
      : [...selectedFacilityIds, id];
    setSelectedFacilityIds(newSelected);
    setAllFacilitiesSelected(newSelected.length === facilities.length);
    setPageNumber(1);
  };

  const getFacilityDropdownLabel = () => {
    if (!selectedFacilityIds || selectedFacilityIds.length === 0) return "No Facilities Selected";
    if (allFacilitiesSelected || selectedFacilityIds.length === facilities.length) return "All Facilities";
    return `${selectedFacilityIds.length} Facilities Selected`;
  };

  const handleReprocess = async (id) => {
    if (!window.confirm("Reprocess this EPCIS file?")) return;
    try {
      setLoading(true);
      await epcisService.reprocessEpcisFile(id);
      const { hide } = cogoToast.success("File reprocessed successfully!!", {
        hideAfter: 15,
        onClick: () => {
          hide();
        },
      });
      fetchFiles(pageNumber, search, selectedFacilityIds);
    } catch (e) {
      const { hide } = cogoToast.error("Something went wrong! Please try again.", {
        hideAfter: 15,
        onClick: () => {
          hide();
        },
      });
      setLoading(false);
    }
  };

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;
    try {
      setLoading(true);
      setDeleteModalOpen(false);
      await epcisService.deleteEpcisFile(fileToDelete.id);
      const { hide } = cogoToast.success("File deleted successfully!!", {
        hideAfter: 15,
        onClick: () => {
          hide();
        },
      });
      setFileToDelete(null);
      if (files.length === 1 && pageNumber > 1) {
        setPageNumber((p) => p - 1);
      } else {
        fetchFiles(pageNumber, search, selectedFacilityIds);
      }
    } catch (e) {
      const { hide } = cogoToast.error("Something went wrong! Please try again.", {
        hideAfter: 15,
        onClick: () => {
          hide();
        },
      });
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setFileToDelete(null);
  };

  return (
    <>
      <CipHeader />
      <div className="bg-white w-11/12 sm:w-10/12 mx-auto mt-12 mb-4">
        <div className="">
          <div
            className={`inline-block customtop z-50 absolute ${
              loading ? "show" : "hidden"
            }`}
          >
            <Loader
              type="Oval"
              color="#00B2A9"
              height={50}
              width={50}
              timeout={300000}
            />
          </div>
          <div className={`inline-block w-full px-10 pb-4 border-b-2`}>
            {/* Section A — Header */}
            <div className="pb-4 border-b-2">
              <ul className="text-left">
                <li className="inline-block text-xs leading-8 w-1/2 align-middle">
                  <span className="text-labelclr font-semibold text-base tracking-wider block">
                    EPCIS FILES
                  </span>
                  <span className="text-fogotpwsd_login text-sm">
                    {totalCount} file{totalCount !== 1 ? "s" : ""} total
                  </span>
                </li>
                <li className="inline-block text-xs leading-8 w-1/2 text-right align-middle">
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    placeholder="Search files..."
                    className="border focus:outline-none h-10 rounded-md pl-3 font-normal text-fogotpwsd_login text-sm w-48 mr-2"
                  />
                  {search && (
                    <button
                      onClick={handleClear}
                      className="bg-header_bg text-white h-10 px-4 rounded text-sm font-medium focus:outline-none mr-2"
                    >
                      Clear
                    </button>
                  )}
                  <div className="relative inline-block align-middle mr-2" ref={dropdownRef}>
                    <button
                      onClick={() => setFacilityDropdownOpen((o) => !o)}
                      className="border bg-white text-fogotpwsd_login h-10 px-3 rounded text-sm font-medium focus:outline-none inline-flex items-center gap-1"
                    >
                      <span>{getFacilityDropdownLabel()}</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {facilityDropdownOpen && (
                      <div className="absolute right-0 mt-1 w-72 bg-white border rounded shadow-lg z-50 max-h-64 overflow-y-auto text-left">
                        <label className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b">
                          <input
                            type="checkbox"
                            checked={allFacilitiesSelected}
                            onChange={handleAllToggle}
                            className="mr-2"
                          />
                          <span className="text-sm font-semibold text-fogotpwsd_login">All</span>
                        </label>
                        {facilities.map((fac) => (
                          <label key={fac.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedFacilityIds ? selectedFacilityIds.includes(fac.id) : false}
                              onChange={() => handleFacilityToggle(fac.id)}
                              className="mr-2"
                            />
                            <span className="text-sm text-fogotpwsd_login">{fac.name} ({fac.code})</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setUploadOpen(true)}
                    className="bg-header_bg text-white h-10 px-4 rounded text-sm font-medium focus:outline-none"
                  >
                    + Upload EPCIS
                  </button>
                </li>
              </ul>
            </div>

            {/* Section B — Table */}
            <div className="pt-5 pb-4">
              {files.length === 0 && !loading ? (
                <div className="text-center py-16">
                  <p className="text-fogotpwsd_login text-sm font-semibold">
                    No EPCIS files found
                  </p>
                  <p className="text-gray-400 text-xs mt-2">
                    Upload an EPCIS XML file using the button above to get started
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-bodybgrnd text-labelclr text-xs uppercase tracking-wider">
                        <th className="px-4 py-3 text-left font-semibold">
                          Document Type
                        </th>
                        <th className="px-4 py-3 text-left font-semibold">
                          Transaction Time
                        </th>
                        <th className="px-4 py-3 text-left font-semibold">
                          Source Owning Party
                        </th>
                        <th className="px-4 py-3 text-left font-semibold">
                          Destination Owning Party
                        </th>
                        <th className="px-4 py-3 text-left font-semibold">
                          Shipment ID
                        </th>
                        <th className="px-4 py-3 text-left font-semibold">
                          Order ID
                        </th>
                        <th className="px-4 py-3 text-center font-semibold">
                          Serials
                        </th>
                        <th className="px-4 py-3 text-left font-semibold">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left font-semibold">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {files.map((file) => (
                        <tr
                          key={file.id}
                          className="border-b text-sm text-fogotpwsd_login hover:bg-gray-50"
                        >
                          <td className="px-4 py-3">
                            <span className="inline-block bg-bodybgrnd text-menuHvrColor px-3 py-1 rounded text-xs font-semibold">
                              {(file.document_type || "").replace(/_/g, " ")}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {file.transaction_time
                              ? moment(file.transaction_time).format(
                                  "MMM DD, YYYY hh:mm A"
                                )
                              : "—"}
                          </td>
                          <td className="px-4 py-3">
                            {file.source_owning_party_name || "—"}
                          </td>
                          <td className="px-4 py-3">
                            {file.destination_owning_party_name || "—"}
                          </td>
                          <td className="px-4 py-3">
                            {file.shipment_id || "—"}
                          </td>
                          <td className="px-4 py-3">
                            {file.order_id ? (
                              <Barcode
                                value={file.order_id}
                                format="CODE128"
                                height={30}
                                width={1}
                                fontSize={10}
                                displayValue={true}
                                margin={0}
                              />
                            ) : "—"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {file.total_serials ?? "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-3 py-1 rounded text-xs font-semibold ${getStatusStyle(
                                file.status
                              )}`}
                            >
                              {file.status || "Unknown"}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() =>
                                history.push("/epcis-viewer/" + file.id)
                              }
                              className="text-menuHvrColor text-xs font-semibold mr-3 hover:underline focus:outline-none"
                              title="View Details"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDeleteClick(file)}
                              className="text-hlred text-xs font-semibold hover:underline focus:outline-none"
                              title="Delete"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Section C — Pagination */}
              {totalCount > PAGE_SIZE && (
                <div className="flex items-center justify-center mt-6 gap-3">
                  <button
                    onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                    disabled={pageNumber === 1}
                    className={`h-9 px-4 rounded text-sm font-medium focus:outline-none ${
                      pageNumber === 1
                        ? "bg-gray-200 text-fogotpwsd_login cursor-not-allowed"
                        : "bg-header_bg text-white"
                    }`}
                  >
                    Previous
                  </button>
                  <span className="text-fogotpwsd_login text-sm">
                    Page {pageNumber} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setPageNumber((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={pageNumber === totalPages}
                    className={`h-9 px-4 rounded text-sm font-medium focus:outline-none ${
                      pageNumber === totalPages
                        ? "bg-gray-200 text-fogotpwsd_login cursor-not-allowed"
                        : "bg-header_bg text-white"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <EpcisUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={() => {
          setUploadOpen(false);
          setPageNumber(1);
          fetchFiles(1, search, selectedFacilityIds);
        }}
      />

      <Modal open={deleteModalOpen} onClose={cancelDelete} center>
        <div className="pt-10 px-8 pb-4">
          <h3
            className="text-lg leading-6 font-medium text-gray-900 text-center"
            id="modal-headline"
          >
            Are you sure you want to delete this EPCIS file?
          </h3>
          {fileToDelete && (
            <p className="text-sm text-fogotpwsd_login text-center mt-2">
              {fileToDelete.original_file_name}
            </p>
          )}
          <div className="flex justify-center">
            <button
              onClick={confirmDelete}
              type="button"
              className="mr-0.5 rounded font-medium h-10 text-white w-auto px-6 bg-secondary bg-login_btn mb-3 mt-4 focus:outline-none"
            >
              Yes
            </button>
            <button
              onClick={cancelDelete}
              type="button"
              className="rounded font-medium h-10 text-white w-auto px-4 bg-secondary bg-login_btn mb-3 mt-4 focus:outline-none"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default withRouter(
  connect(
    () => ({}),
    (dispatch) => {
      return bindActionCreators({}, dispatch);
    }
  )(EpcisViewer)
);